"use client"

import { TbRefresh, TbPrinter, TbDownload, TbChartBar } from "react-icons/tb"
import { PROFILES } from "./data"

export type CostDashboardProps = {
   activeConcept: string
   activeMarketLevel: "low" | "medium" | "high"
   totalStartupCost: number
   termYears: number
   onTermYearsChange: (value: number) => void
   interestRate: number
   onInterestRateChange: (value: number) => void
   ticketSize: number
   onTicketSizeChange: (value: number) => void
   totalInterest: number
   totalRepayment: number
   monthlyPayment: number
   dailyOverhead: number
   customersNeeded: number
   validTicket: number
   onReset: () => void
}

export default function CostDashboard({
   activeConcept,
   activeMarketLevel,
   totalStartupCost,
   termYears,
   onTermYearsChange,
   interestRate,
   onInterestRateChange,
   ticketSize,
   onTicketSizeChange,
   totalInterest,
   totalRepayment,
   monthlyPayment,
   dailyOverhead,
   customersNeeded,
   validTicket,
   onReset,
}: CostDashboardProps) {
   return (
      <div className="mb-8 rounded-2xl bg-[#0F172A] p-5 text-white sm:p-9">
         <div className="mb-8 flex flex-col items-start justify-between gap-5 sm:flex-row">
            <div>
               <div className="text-custom-lg-bold uppercase">Estimated Total Startup Cost</div>
               <div className="my-1 text-custom-4xl-bold leading-tight tracking-tight text-white ">
                  ${totalStartupCost.toLocaleString()}
               </div>
               <div className="text-custom-sm-semibold">
                  {PROFILES[activeConcept].label} • {activeMarketLevel} cost
               </div>
            </div>
            <div className="flex w-full items-center gap-2 sm:w-auto">
               <button
                  type="button"
                  onClick={onReset}
                  className="flex items-center gap-1.5 rounded-md border border-[#334155] bg-[#1E293B] px-3 py-2 text-custom-sm-medium transition-colors hover:bg-[#334155] sm:px-4"
               >
                  <TbRefresh className="h-5 w-5" /> <span className="hidden sm:inline">Reset</span>
               </button>
               <button
                  type="button"
                  onClick={() => window.print()}
                  className="rounded-md border border-[#334155] bg-[#1E293B] p-2 text-slate-200 transition-colors hover:bg-[#334155] "
               >
                  <TbPrinter className="h-5 w-5" />
               </button>
               <button
                  type="button"
                  onClick={() => window.print()}
                  className="flex items-center gap-1.5 rounded-md border border-[#f15a29] bg-[#f15a29] px-3 py-2 text-custom-sm-medium transition-colors hover:bg-[#d4481d] sm:px-4"
               >
                  <TbDownload className="h-5 w-5" /> <span className="hidden sm:inline">Export</span>
               </button>
            </div>
         </div>

         <div className="mb-8 border-t border-[#1E293B] pt-6">
            <div className="mb-2.5 text-custom-lg-bold">
               Loan Duration: <span className="text-[#f15a29]">{termYears} years</span>
            </div>
            <div className="flex items-center">
               <input
                  type="range"
                  min="2"
                  max="15"
                  step="1"
                  value={termYears}
                  onChange={(e) => onTermYearsChange(Number(e.target.value))}
                  className="w-full cursor-pointer accent-[#f15a29]"
               />
            </div>
         </div>

         <div className="mb-8 grid grid-cols-1 gap-4 text-center sm:grid-cols-3">
            <div>
               <div className="text-custom-lg-bold text-white">${monthlyPayment.toLocaleString()}</div>
               <div className="mt-0.5 text-custom-sm-medium">Monthly Payment</div>
            </div>
            <div>
               <div className="text-custom-lg-bold text-white">${totalInterest.toLocaleString()}</div>
               <div className="mt-0.5 flex flex-col items-center justify-center text-custom-sm-medium">
                  <span>Total Interest</span>
                  <div className="mt-1 inline-flex items-center gap-1">
                     <span>(</span>
                     <input
                        type="number"
                        min="1"
                        max="25"
                        step="0.5"
                        value={interestRate}
                        onChange={(e) => onInterestRateChange(parseFloat(e.target.value))}
                        className="w-14 rounded border border-[#334155] bg-white/10 px-1 py-0.5 text-center text-custom-sm-medium text-white outline-none focus:border-[#f15a29]"
                     />
                     <span>%)</span>
                  </div>
               </div>
            </div>
            <div>
               <div className="text-custom-lg-bold text-white">${totalRepayment.toLocaleString()}</div>
               <div className="mt-0.5 text-custom-sm-medium">Total Repayment</div>
            </div>
         </div>

         <div className="flex flex-col items-center justify-between gap-2 rounded-md bg-[#065F46] p-3.5 px-4 text-custom-base-medium  text-white sm:flex-row">
            <div className="flex flex-wrap items-center gap-2">
               <TbChartBar className="h-5 w-5 shrink-0 text-[#34D399]" />
               <span>
                  To cover this: ${dailyOverhead.toLocaleString()}/day ~= {customersNeeded} customers at ${validTicket}
               </span>
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-center gap-1.5">
               <span>Average Ticket:</span>
               <div className="inline-flex items-center gap-0.5 rounded bg-white/15 px-2 py-0.5">
                  <span>$</span>
                  <input
                     type="number"
                     min="2"
                     max="500"
                     value={ticketSize}
                     onChange={(e) => onTicketSizeChange(Number(e.target.value))}
                     className="w-16 border-none bg-transparent px-1 text-center text-custom-base-medium text-white outline-none"
                  />
               </div>
            </div>
         </div>
      </div>
   )
}
